import { useEffect, useState } from "react"
import { useSearchParams } from "react-router-dom"
import {
  clearStudentSession,
  saveStudentSession,
  validateLearnerSession,
} from "../../api/studentAuth"
import type { StudentInfo } from "../../auth/learner/types"
import {
  LEARNER_DEFAULT_PATH,
  LEARNER_LOGIN_PATH,
} from "../../auth/learner/constants"
import LearnerAuthBootScreen from "../../auth/learner/LearnerAuthBootScreen"

function decodeStudentFromToken(token: string): StudentInfo | null {
  try {
    const payloadPart = token.split(".")[1]
    if (!payloadPart) return null
    const json = atob(payloadPart.replace(/-/g, "+").replace(/_/g, "/"))
    const payload = JSON.parse(json) as Record<string, unknown>
    if (!payload.email || !payload.id) return null
    return {
      id: String(payload.id),
      first_name: String(payload.first_name || ""),
      last_name: String(payload.last_name || ""),
      email: String(payload.email),
      grade: (payload.grade as string | null | undefined) ?? null,
      role: "student",
    }
  } catch {
    return null
  }
}

/**
 * Grinds360 SSO landing page.
 * Expects: /thirdparty/login?token=...&refreshToken=...&platform=Grinds360
 */
const ThirdPartyLogin = () => {
  const [params] = useSearchParams()
  const [error, setError] = useState("")

  useEffect(() => {
    let cancelled = false

    const run = async () => {
      const token = params.get("token")
      const refreshToken = params.get("refreshToken")

      if (!token || !refreshToken) {
        setError("Missing login tokens. Please try again from Grinds360.")
        return
      }

      const decoded = decodeStudentFromToken(token)
      if (!decoded) {
        setError("Invalid login token. Please try again from Grinds360.")
        return
      }

      try {
        saveStudentSession(token, refreshToken, decoded)
        await validateLearnerSession()
        if (!cancelled) {
          window.location.replace(LEARNER_DEFAULT_PATH)
        }
      } catch {
        clearStudentSession()
        if (!cancelled) {
          setError("Unable to complete Exam Creator login. Please try again.")
        }
      }
    }

    run()
    return () => {
      cancelled = true
    }
  }, [params])

  if (error) {
    return (
      <div
        data-theme="learner"
        style={{
          minHeight: "100vh",
          display: "grid",
          placeItems: "center",
          padding: 24,
          fontFamily: "Inter, system-ui, sans-serif",
        }}
      >
        <div style={{ maxWidth: 420, textAlign: "center" }}>
          <h1 style={{ fontSize: 22, marginBottom: 12 }}>Sign-in failed</h1>
          <p style={{ color: "#5b6472", marginBottom: 20 }}>{error}</p>
          <a href={LEARNER_LOGIN_PATH}>Go to learner login</a>
        </div>
      </div>
    )
  }

  return <LearnerAuthBootScreen />
}

export default ThirdPartyLogin
